import { RoleSpecification } from "@src/types";
import { message } from "antd";

import { baseApiSlice } from "./baseApiSlice";
import { EndpointBuilder } from "@reduxjs/toolkit/query";

export const roleSpecificationsApi = baseApiSlice.injectEndpoints({
  endpoints: (builder: EndpointBuilder<any, any, any>) => ({
    /* GET endpoints */
    ...getEndpoints(builder),
    /* POST endpoints */
    ...postEndpoints(builder),
    /* PUT endpoints */
    ...putEndpoints(builder),
    /* DELETE endpoints */
    ...deleteEndpoints(builder),
  }),
});

function getEndpoints(builder: EndpointBuilder<any, any, any>) {
  return {
    /**
     * Get all role specifications
     */
    getRoleSpecifications: builder.query<RoleSpecification[], void>({
      query: () => "role-specifications",
      transformErrorResponse: async (): Promise<void> => {
        message.error("Unable to load standard roles", 5);
      },
      providesTags: ["RoleSpecifications"],
    }),
  };
}

function postEndpoints(builder: EndpointBuilder<any, any, any>) {
  const baseMethod = "POST";

  return {
    /**
     * Create a new role specification with the given data
     */
    addRoleSpecification: builder.mutation<void, RoleSpecification>({
      query: (roleSpecification) => ({
        url: "role-specifications",
        method: baseMethod,
        body: roleSpecification,
      }),
      transformErrorResponse: async (): Promise<void> => {
        message.error("Error creating new role specification", 5);
      },
      invalidatesTags: ["RoleSpecifications", "StandardRoles"],
    }),
  };
}

function putEndpoints(builder: EndpointBuilder<any, any, any>) {
  const baseMethod = "PUT";
  return {
    /**
     * updates the role specification
     */
    updateRoleSpecification: builder.mutation<void, RoleSpecification>({
      query: (roleSpecification) => ({
        url: `role-specifications/${roleSpecification.roleSpecificationId}`,
        method: baseMethod,
        body: roleSpecification,
      }),
      transformErrorResponse: async (): Promise<void> => {
        message.error("Error updating role specification", 5);
      },
      invalidatesTags: ["RoleSpecifications", "StandardRoles"],
    }),
  };
}

function deleteEndpoints(builder: EndpointBuilder<any, any, any>) {
  const baseMethod = ["DELETE"];

  return {
    /**
     * Delete a single role specification by a given id
     */
    deleteRoleSpecification: builder.mutation<void, number>({
      query: (id) => ({
        url: `role-specifications/${id}`,
        method: baseMethod,
      }),
      transformErrorResponse: async (): Promise<void> => {
        message.error("Error deleting role specification", 5);
      },
      invalidatesTags: ["RoleSpecifications", "StandardRoles"],
    }),
  };
}

export const {
  /* GET endpoints */
  useGetRoleSpecificationsQuery,
  /* POST endpoints */
  useAddRoleSpecificationMutation,
  /* PUT endpoints */
  useUpdateRoleSpecificationMutation,
  /* DELETE endpoints */
  useDeleteRoleSpecificationMutation,
} = roleSpecificationsApi;
